// Strings of the networking UI shared by its pages (feature spec 17, phase 1; spec 11 6.15). Composed into
// `strings.networks` in `src/strings.ts`; each part of the networking UI keeps its own file next to this one.
import type { AttachmentSize, AttachmentState, NetworkStatus, RunKind } from '../../api/types';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const networkShared = {
  plural,
  switchedOff: 'Networking is switched off in this installation.',
  goToBlueprints: 'Go to Blueprints',
  requiresNetworkAdmin: 'Requires the network admin role.',
  alsoNeedsOperator: 'Also needs the operator role',
  onlyAdminsAttach: (network: string) => `Only network admins attach to ${network}`,
  askAdminToApply: (network: string) => `Ask a network admin to apply ${network}.`,
  nav: {
    networks: 'Networks',
    goNetworks: 'Go to Networks',
    allNetworks: 'All networks',
    kind: 'Network',
    goToNetwork: 'Go to network…',
    planNetwork: 'Plan network…',
    attachToNetwork: 'Attach deployments to network…',
    chooseNetwork: 'Choose a network',
    noNetworks: 'No networks yet',
  },
  providers: { azure: 'Azure', aws: 'AWS', gcp: 'Google Cloud', cloudflare: 'Cloudflare' } as Record<
    string,
    string
  >,
  status: {
    new: 'New',
    active: 'Active',
    failed: 'Failed',
    destroyed: 'Destroyed',
  } satisfies Record<NetworkStatus, string>,
  attachmentState: {
    pending: 'Pending',
    'spoke-ready': 'Waiting for the connect run',
    connecting: 'Connecting',
    connected: 'Connected',
    detaching: 'Detaching',
    detached: 'Detached',
    failed: 'Failed',
  } satisfies Record<AttachmentState, string>,
  waitsForApply: 'Waits for the network’s apply',
  retained: 'Retained',
  settingsChanged: 'Settings changed',
  sizes: {
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    xlarge: 'Extra large',
  } satisfies Record<AttachmentSize, string>,
  /** "Small · /23 · 512 addresses". */
  sizeOption: (label: string, prefix: number, addresses: number) =>
    `${label} · /${prefix} · ${addresses.toLocaleString('en-US')} addresses`,
  recommended: 'recommended',
  // Waiting runs (6.15.17).
  waiting: {
    forRun: (target: string, runKind: string) => `Waiting for ${target}’s ${runKind}`,
    started: (since: string) => `, started ${since}`,
    by: (by: string) => ` by ${by}`,
    retrying: (until: string) => `Retrying at ${until} (waiting for permissions)`,
    retryingSoon: 'Retrying soon (waiting for permissions)',
    networkingDisabled: 'Waiting: networking is switched off in this installation.',
    queued: 'Queued',
    grantPendingFailed: (retry: string) => `Failed · ${retry.charAt(0).toLowerCase()}${retry.slice(1)}`,
    grantPendingCallout: (deployment: string) =>
      `The network’s permissions on ${deployment}’s network weren’t active yet. Nebisys retries the connect run automatically.`,
  },
  runKindLower: { plan: 'plan', apply: 'apply', destroy: 'destroy', refresh: 'refresh' } satisfies Record<
    RunKind,
    string
  >,
  // Run labels (6.15.11).
  runLabels: {
    connect: (deployment: string) => `Connect ${deployment}`,
    detach: (deployment: string) => `Detach ${deployment}`,
    decision: (deployment: string) => `Decision for ${deployment}`,
    aDeployment: 'a deployment',
    queuedAfter: 'queued after',
    attachment: 'Attachment',
    nebisys: 'Nebisys',
    nebisysAttachment: 'Nebisys (attachment)',
  },
  // States and errors (6.15.18).
  errors: {
    applyNeeded: (network: string) =>
      `Needs a reviewed network apply: ask a network admin to plan ${network}.`,
    destroyed: (network: string, deployment: string) =>
      `Ask a network admin to apply ${network} again, or detach ${deployment}.`,
    attachNotPermitted: (network: string) => `Only network admins can attach to ${network}.`,
    regionNotEnabled: (network: string, region: string) =>
      `${network} doesn’t include ${region}; a network admin can add it.`,
    runPending: (network: string) => `${network} already has a plan or apply queued.`,
    stepsUnavailable: 'What the connect run does couldn’t be computed; the interruption still applies.',
    notApplied: (network: string) => `Apply ${network} first.`,
    accountNotAllowed: (account: string) => `Ask a network admin to allow ${account}.`,
    detachFailed: (network: string) => `The detach from ${network} failed, so nothing was destroyed.`,
    detachUnexpectedChange: (deployment: string) => `Private name resolution is off: apply ${deployment}.`,
    changeNeedsDetach: (network: string, deployment: string) =>
      `Detach from ${network} first: ${deployment} can’t move between networks directly.`,
    retainedElsewhere: (deployment: string, network: string) =>
      `${deployment} keeps addresses of ${network}: release them and apply first.`,
    hasAttachments: 'Detach the attached deployments first.',
    notRemovable: 'Only a new or destroyed network without attachments can be deleted.',
    hasRetained: (network: string) =>
      `Deployments still keep addresses of ${network}; they have to release them first.`,
    stateChanged: 'The attachment changed meanwhile; this shows its current state.',
    awsDatabasePrivate: (deployment: string) => `Public access is off while ${deployment} is attached.`,
  },
  actions: {
    planNetwork: 'Plan network',
    openSettings: (network: string) => `Open ${network} settings`,
    openDeploymentSettings: (deployment: string) => `Open ${deployment} settings`,
    addSupernet: 'Add a supernet',
    openDetachRun: 'Open the detach run',
    apply: 'Apply…',
    detach: 'Detach',
    showAttachments: 'Show attachments',
    retry: 'Retry',
    openRun: 'Open run',
  },
};
